/* eslint-disable react-hooks/exhaustive-deps */
import { CanvasDragEvent, CanvasPressEvent, Point } from "openseadragon";
import { useEffect, useRef, useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";

import { User } from "pwapi";

import {
  PenDrawingAnnotation,
  ScaleInfo,
  ViewerMode,
} from "custom-models/ViewerTypes";

import { changeViewerMode } from "shared/actions/slideDetailActions";
import {
  createPenDrawingAnnotation,
  lineTo,
  moveTo,
} from "shared/utils/annotations";

import PenDrawing from "./PenDrawing";
import ViewerFacade from "../ViewerFacade";

interface PenDrawingToolProps {
  scaleInfo: ScaleInfo;
  viewerFacade: ViewerFacade;
  user: User;
  newPenDrawing?: PenDrawingAnnotation;
  startNewPenDrawing: Function;
  onAnnotationComplete: (ann: PenDrawingAnnotation) => void;
}

const PenDrawingTool = (props: PenDrawingToolProps) => {
  const {
    scaleInfo,
    viewerFacade,
    user,
    newPenDrawing,
    startNewPenDrawing,
    onAnnotationComplete,
  } = props;

  const dispatch = useDispatch();

  const newPenDrawingRef = useRef<PenDrawingAnnotation | undefined>(undefined);

  if (newPenDrawing) {
    newPenDrawingRef.current = newPenDrawing;
  }

  const lastPointX = useRef<number | undefined>(undefined);
  const lastPointY = useRef<number | undefined>(undefined);
  const dragging = useRef<boolean>(false);
  const drewLineSegments = useRef<boolean>(false);

  const wrapUpNewAnnotation = useCallback(() => {
    if (newPenDrawingRef.current) {
      if (drewLineSegments.current) {
        onAnnotationComplete(newPenDrawingRef.current);
        drewLineSegments.current = false;
      }
      dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
    }
  }, [viewerFacade, onAnnotationComplete, dispatch]);

  useHotkeys(
    "Return",
    () => {
      wrapUpNewAnnotation();
    },
    []
  );

  useEffect(() => {
    const pointWithinImage = (point: Point) =>
      point.x >= 0 &&
      point.y >= 0 &&
      point.x < scaleInfo.width &&
      point.y < scaleInfo.height;

    const drawLineIfFarEnough = (
      event: CanvasPressEvent | CanvasDragEvent,
      minSegmentLength: number
    ) => {
      const point = viewerFacade.imageCoords(event);

      if (
        point &&
        pointWithinImage(point) &&
        newPenDrawingRef.current &&
        dragging.current &&
        lastPointX.current !== undefined &&
        lastPointY.current !== undefined &&
        (Math.abs(point.x - lastPointX.current) > minSegmentLength ||
          Math.abs(point.y - lastPointY.current) > minSegmentLength)
      ) {
        lastPointX.current = point.x;
        lastPointY.current = point.y;
        drewLineSegments.current = true;
        startNewPenDrawing(lineTo(newPenDrawingRef.current, point));
      }
    };

    const pressHandlerId = viewerFacade.addHandler("canvas-press", (event) => {
      const point = viewerFacade.imageCoords(event);
      if (point && pointWithinImage(point)) {
        lastPointX.current = point.x;
        lastPointY.current = point.y;
        dragging.current = true;
        drewLineSegments.current = false;
        if (!newPenDrawingRef.current) {
          startNewPenDrawing(
            createPenDrawingAnnotation(
              undefined,
              "",
              user,
              null,
              `M ${Math.round(point.x)} ${Math.round(point.y)}`,
              false
            )
          );
        } else {
          startNewPenDrawing(moveTo(newPenDrawingRef.current, point));
        }
      }
    });

    const dragHandlerId = viewerFacade.addHandler("canvas-drag", (event) => {
      drawLineIfFarEnough(event, 5 / scaleInfo.scaleX);
    });

    const releaseHandlerId = viewerFacade.addHandler(
      "canvas-release",
      (event) => {
        drawLineIfFarEnough(event, 2 / scaleInfo.scaleX);
        dragging.current = false;
        lastPointX.current = undefined;
        lastPointX.current = undefined;
      }
    );

    const exitHandlerId = viewerFacade.addHandler("canvas-exit", (event) => {
      if (event.pointerType === "pen" || event.pointerType === "touch") {
        // Unlike releasing a mouse, lifting a finger or an Apple Pencil after one stroke will fire an exit event.
        return;
      }
      wrapUpNewAnnotation();
    });

    return () => {
      viewerFacade.removeHandler(pressHandlerId);
      viewerFacade.removeHandler(dragHandlerId);
      viewerFacade.removeHandler(releaseHandlerId);
      viewerFacade.removeHandler(exitHandlerId);
    };
  }, [
    viewerFacade,
    scaleInfo.scaleX,
    scaleInfo.height,
    scaleInfo.width,
    startNewPenDrawing,
    user,
  ]);

  if (newPenDrawing) {
    return (
      <>
        <g x={0} y={0} width={scaleInfo.width} height={scaleInfo.height} />
        <PenDrawing
          annotation={newPenDrawing}
          viewerFacade={viewerFacade}
          scaleInfo={scaleInfo}
        />
      </>
    );
  }
  return <></>;
};

export default PenDrawingTool;
